import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Zap,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Crown,
  LogOut,
  Loader2,
  FileText,
  Settings,
  Clock,
} from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

export default function SingleCheckDashboard() {
  const { user, logout } = useAuth();
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const credits = user?.paid_credits || 0;

  useEffect(() => {
    api.get('/quick-check/history')
      .then(r => setChecks(r.data?.checks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="single-check-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            <span className="font-black text-base tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
            <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-[10px] font-mono ml-2">BID CHECK</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app/billing">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900 text-xs h-8" data-testid="single-billing-btn">
                <CreditCard className="w-3.5 h-3.5 mr-1" /> Billing
              </Button>
            </Link>
            <Link to="/app/settings">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900 text-xs h-8" data-testid="single-settings-btn">
                <Settings className="w-3.5 h-3.5 mr-1" /> Settings
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400 hover:text-red-600 h-8" data-testid="single-logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Credit status */}
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Available Credits</p>
                <p className="text-3xl font-bold font-mono text-slate-900 mt-1" data-testid="credit-count">{credits}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {credits > 0 ? 'Ready to use on your next bid check' : 'Purchase a credit to run a bid check'}
                </p>
              </div>
              <Link to="/check">
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold h-11"
                  disabled={credits < 1}
                  data-testid="run-check-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {credits > 0 ? 'Run Bid Check' : 'No Credits'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade CTA */}
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-slate-900">Upgrade to BidSentry Pro</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Unlimited bid checks, full dashboard with portfolio analytics, margin trends, and historical bid tracking. £149/month.
                </p>
              </div>
              <Link to="/pricing">
                <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50 h-8 text-xs" data-testid="upgrade-pro-btn">
                  View Pro <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Check History */}
        <div>
          <h2 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <FileText className="w-4 h-4 text-slate-400" /> YOUR BID CHECKS
          </h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
            </div>
          ) : checks.length === 0 ? (
            <Card className="border-dashed border-slate-300 bg-slate-50">
              <CardContent className="p-8 text-center">
                <p className="text-sm text-slate-500">No bid checks yet.</p>
                <p className="text-xs text-slate-400 mt-1">Run your first check to see results here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {checks.map((c, i) => (
                <Card key={c.id || i} className="border-slate-200 bg-white hover:bg-slate-50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            £{(c.contract_value || 0).toLocaleString()} contract — {c.result?.risk_classification || 'Analysed'}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3" /> {fmtDate(c.created_at)}
                            {c.result?.margin_pct != null && (
                              <span className="ml-2 font-mono">{c.result.margin_pct.toFixed(1)}% margin</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {c.result?.health_score ? `Score: ${c.result.health_score}` : 'Complete'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Buy more credits */}
        {credits === 0 && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-5 text-center">
              <p className="text-sm text-slate-600 mb-3">Need another check?</p>
              <Link to="/check">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="buy-more-btn">
                  Buy Bid Check — £9
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
